import { sortBy } from "lodash-es";
import { type FC, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { HeaderDivider, SymbolIcon, TriggerButton } from "widgets";

import { ANNOTATION_TYPE, CELL_TYPE, DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.js";

import { getDataSeriesKey } from "../../app-utils/chart-worker.js";
import { pluralizeWord } from "../../app-utils/string.js";
import {
  type ColumnAnnotation,
  type DataRow,
  type DatasetColumn,
  type InputTableQuerySpec,
  type StringMapEntry,
} from "../../graphql.js";
import {
  chartTableSelectionModelUpdated,
  type IDatasetViewerState,
  SAMPLES_ANNOTATION,
  type SelectedDataSeries,
  setChartSelection,
  splitPaneLayoutStateChanged,
} from "../../redux/modules/dataset-viewer.js";
import { createTempGroup } from "../../redux/modules/saved-group.js";
import { type ITableColumn } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";

export interface IDatasetViewerTableOwnProps {
  groupId: number;
}

/**
 * The dataset viewer table
 */
const DatasetViewerTable: FC<IDatasetViewerTableOwnProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();

  // client side grid component instance for getting the list of selected identifiers or access to some more specific behaviour
  const tableInstance = useRef<InMemoryTableWithSelectionModel>(undefined);
  const {
    dataset,
    selectedAnnotations,
    selectedDataSeries,
    chartSelection,
    dataSeriesMap,
    chartTableSelectionModel,
    splitPaneLayout,
  } = useSelector<IAppState, IDatasetViewerState>((state) => state.datasetViewer);

  const datasetColumns: DatasetColumn[] = useMemo(() => {
    if (dataset) {
      return chartSelection.length > 0
        ? // if chartSelection is not empty we return only the columns selected on the chart
          dataset.columns.filter(
            (column: DatasetColumn) => !!chartSelection.find((field: string) => field === column.field),
          )
        : dataset.columns;
    }
    return [];
  }, [dataset, chartSelection]);

  const annotations: ColumnAnnotation[] = useMemo(() => {
    if (dataset) {
      return (
        selectedAnnotations
          // special case for static condition "Samples", we do not want this one
          .filter((ca: ColumnAnnotation) => ca.field !== SAMPLES_ANNOTATION.field)
      );
    }
    return [];
  }, [dataset, selectedAnnotations]);

  const dataseries: SelectedDataSeries[] = useMemo(() => {
    if (dataset) {
      return (
        selectedDataSeries
          // filter out the hidden data series and the ones that are created from a split by condition
          .filter((ds: SelectedDataSeries) => !ds.hidden && !ds.splitParent)
      );
    }
    return [];
  }, [dataset, selectedDataSeries]);

  const datasetTableColumns = useMemo((): ITableColumn[] => {
    const columns: any[] = [];

    if (dataset) {
      // special case for the sample name, always provided
      columns.push({
        name: "Sample",
        field: "name",
        cellType: CELL_TYPE.STRING,
      });
      annotations.forEach((ca: ColumnAnnotation) => {
        columns.push({
          name: ca.name,
          field: ca.field,
          cellType: ca.type === ANNOTATION_TYPE.NUMERICAL ? CELL_TYPE.NUMBER : CELL_TYPE.STRING,
        });
      });
      dataseries.forEach((ds: SelectedDataSeries) => {
        columns.push({
          name: ds.name,
          field: getDataSeriesKey(ds.name),
          cellType: CELL_TYPE.NUMBER,
        });
      });
    }

    return columns;
  }, [dataset, annotations, dataseries]);

  const rowData: any[] = useMemo(() => {
    const rows: any[] = [];

    if (dataset) {
      const samples: DatasetColumn[] = datasetColumns;

      samples
        // create the rows from the raw data
        .forEach((column: DatasetColumn) => {
          const row = { id: column.field, name: column.name };
          // iterate through the selected column conditions to set the field names
          annotations.forEach((ca: ColumnAnnotation) => {
            const annotationValues: StringMapEntry[] = column.annotations.filter(
              (annotation: StringMapEntry) => annotation.key === ca.field,
            );
            row[ca.field] = sortBy(annotationValues, "multiValueIndex").map((annotationValue) => annotationValue.value);
          });

          // now make a table column for the values of each selected data series
          dataseries.forEach((ds: SelectedDataSeries) => {
            // multiple rows are possible therefore we simply join the values in the cell
            const dataRows: DataRow[] = dataSeriesMap[ds.name]?.rows;
            const entries: StringMapEntry[] = dataRows
              ?.map((dataRow: DataRow) => {
                return dataRow.values.find((value: StringMapEntry) => value.key === column.field);
              })
              .filter((entry) => !!entry);

            row[getDataSeriesKey(ds.name)] = sortBy(entries, "multiValueIndex").map((entry) => entry.value);
          });
          rows.push(row);
        });
    }

    return rows;
  }, [datasetColumns, annotations, dataseries, dataSeriesMap]);

  const allRowIds: string[] = useMemo(() => {
    return dataset ? datasetColumns.map((column: DatasetColumn) => column.field) : [];
  }, [datasetColumns, dataset]);

  const createGroupFromTableSelection = useCallback(() => {
    const selectedIds = tableInstance.current.getSelectedIds() as number[];

    const tableQuerySpec: InputTableQuerySpec = {
      id: props.groupId,
      idType: DATATABLE_ID_TYPE.GROUP,
      resultType: ENTITY_TYPE.SAMPLE,
      restrictions: {
        selection: {
          selectedResultIds:
            selectedIds.length > 0
              ? selectedIds
              : datasetColumns.map((datasetColumn: DatasetColumn) => datasetColumn.field), // all of the table
        },
      },
    };

    return dispatch(createTempGroup(tableQuerySpec)).then((sampleGroupId: number) => {
      navigate(`/visualizer/data/group/${props.groupId}/table/${sampleGroupId}`);
    });
  }, [tableInstance, props.groupId, datasetColumns]);

  const samplesSize: number = dataset?.columns.length || 0;
  const selectionSize: number = chartSelection.length > 0 ? chartSelection.length : samplesSize;

  return (
    <div className="inner" style={{ height: "100%" }}>
      <PageHeader
        title={pluralizeWord("sample", selectionSize)}
        badge={`${selectionSize} displayed out of ${samplesSize}`}
        actionWidgets={[
          selectionSize < samplesSize && (
            <TriggerButton
              key={"dataset-viewer-table-action-1"}
              icon={"visibility"}
              tooltip={"Show all samples"}
              onPress={() => dispatch(setChartSelection([]))}
              label={"Show All"}
              showLabel
            />
          ),
          <TriggerButton
            key={"dataset-viewer-table-action-2"}
            icon={"view_column"}
            tooltip={"View a grid with the data of your selection"}
            onPress={createGroupFromTableSelection}
            label={"View"}
            showLabel
          />,
          <HeaderDivider key={"dataset-viewer-table-action-3"} />,
          <TriggerButton
            tooltip={"Hide Data Table"}
            shortcut={"Shift+T"}
            icon={
              <SymbolIcon icon={splitPaneLayout.verticalLayout ? "bottom_panel_close" : "right_panel_close"} filled />
            }
            onPress={() => dispatch(splitPaneLayoutStateChanged({ ...splitPaneLayout, hidden: true }))}
            key={"dataset-viewer-table-action-4"}
            label={"Hide"}
            showLabel
          />,
        ]}
      />
      <div style={{ height: "calc(100% - 50px)" }}>
        <InMemoryTableWithSelectionModel
          ref={tableInstance}
          columns={datasetTableColumns}
          rowGridData={rowData}
          uniqueGridIdentifier={"dataset-charted-rows"}
          useSelectionModel={true}
          initialSelectionModel={chartTableSelectionModel}
          selectionModelUpdated={(sm) => dispatch(chartTableSelectionModelUpdated(sm))}
        />
      </div>
    </div>
  );
};

export default DatasetViewerTable;
